import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { CableIcon, FileText, FolderIcon, Globe, Table } from "lucide-react";

import NewWorkspaceLayout from "../../layout/NewWorkspaceLayout";
import Spinner from "../../../../assets/icons/Spinner";
import TrashIcon from "../../../../assets/icons/TrashIcon";
import Message from "../../../../i18n/Message";
import ChevronLeftIcon from "../../../../assets/icons/ChevronLeftIcon";
import { linkToKnowledgeBase, linkToKnowledgeBases } from "../../../../routes";
import { kbaseSelectors } from "../store/kbaseReducer";
import {
  deleteKBase,
  loadKBase,
  loadKBases,
} from "../store/kbaseActionCreators";
import KBaseTitle from "./KBaseTitle";
import KBaseTab from "./kbaseTab";
import FilesContentPanel from "./files/FilesContentPanel";
import WebpagesContentPanel from "./webpages/WebpagesContentPanel";
import TablesContentPanel from "./tables/TablesContentPanel";
import IntegrationsContentPanel from "./integrations/IntegrationsContentPanel";

import "./KBaseDetailsPage.scss";

const KBaseDetailsPage = () => {
  const { workspaceId, kbaseId, tabId } = useParams();
  const intl = useIntl();
  const nav = useNavigate();
  const dispatch = useDispatch();
  const kbase = useSelector(kbaseSelectors.getKBase);
  const [tab, setTab] = useState(tabId || KBaseTab.FILES);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (!kbase || (kbase && kbase.uid !== kbaseId)) {
      console.log("Loading knowledge base ", kbaseId);
      dispatch(loadKBase(intl, workspaceId, kbaseId));
    }
  }, [dispatch, intl, workspaceId, kbaseId, kbase]);

  useEffect(() => {
    if (tabId && tabId !== tab) {
      setTab(tabId);
    }
  }, [tabId, tab]);

  const onTabChange = (newTab) => {
    setTab(newTab);
    nav(linkToKnowledgeBase(workspaceId, kbaseId, newTab));
  };

  const onDeleteConfirmed = () => {
    const onSuccess = () => {
      setShowDeleteConfirm(false);
      dispatch(loadKBases(intl, workspaceId));
      nav(linkToKnowledgeBases(workspaceId));
    };
    dispatch(
      deleteKBase(intl, workspaceId, kbase.uid, onSuccess, () =>
        setShowDeleteConfirm(false)
      )
    );
  };

  return (
    <NewWorkspaceLayout contentPanelClassName="kbase_details_page">
      {(!kbase || (kbase && kbase.uid !== kbaseId)) && (
        <div className="spinner_container">
          <Spinner />
        </div>
      )}
      {kbase && kbase.uid === kbaseId && (
        <>
          <div className="title_row">
            <div className="title_column">
              <div className="breadcrumbs">
                <div onClick={() => nav(linkToKnowledgeBases(workspaceId))}>
                  <ChevronLeftIcon
                    size={16}
                    strokeWidth="2"
                    color="rgb(103, 116, 142)"
                  />
                  <Message id="menu_knowledge" />
                </div>
              </div>
              <div className="title_section">
                <KBaseTitle kbase={kbase} workspaceId={workspaceId} />
                <div className="title_actions">
                  <div className="confirm_container">
                    {showDeleteConfirm && (
                      <>
                        <div
                          className="cover"
                          onClick={() => setShowDeleteConfirm(false)}
                        ></div>
                        <div className="confirm_panel">
                          <button
                            className="btn bg-gradient-primary btn-sm"
                            onClick={onDeleteConfirmed}
                          >
                            <Message id="confirm_delete" />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                  <button
                    className="btn no-btn tile-action"
                    onClick={() => setShowDeleteConfirm(!showDeleteConfirm)}
                  >
                    <TrashIcon size="16" color="#344767" />
                    <Message id="delete" />
                  </button>
                </div>
              </div>
              <div className="undertitle_row">
                <div className="metadata_column">
                  <div className="metadata_row">
                    <div className="meta_name">
                      <Message id="created_on" />
                    </div>
                    <div className="meta_value">
                      {dayjs(kbase.dateCreated).format("DD MMM")}
                    </div>
                  </div>
                </div>
                <div className="tabs_column">
                  <div className="tabs_container">
                    <div className="tab_group">
                      <div
                        className={
                          "tab_group_item " +
                          (tab === KBaseTab.FILES ? " selected " : "")
                        }
                        onClick={() => onTabChange(KBaseTab.FILES)}
                      >
                        <FolderIcon size={18} color="#344767" />
                        <Message id="kbase_tab_files" />
                      </div>
                      <div
                        className={
                          "tab_group_item " +
                          (tab === KBaseTab.WEBPAGES ? " selected " : "")
                        }
                        onClick={() => onTabChange(KBaseTab.WEBPAGES)}
                      >
                        <Globe size={18} color="#344767" />
                        <Message id="kbase_tab_webpages" />
                      </div>
                      <div
                        className={
                          "tab_group_item " +
                          (tab === KBaseTab.TABLES ? " selected " : "")
                        }
                        onClick={() => onTabChange(KBaseTab.TABLES)}
                      >
                        <Table size={18} color="#344767" />
                        <Message id="kbase_tab_tables" />
                      </div>
                      <div
                        className={
                          "tab_group_item " +
                          (tab === KBaseTab.INTEGRATIONS ? " selected " : "")
                        }
                        onClick={() => onTabChange(KBaseTab.INTEGRATIONS)}
                      >
                        <CableIcon size={18} color="#344767" />
                        <Message id="kbase_tab_integrations" />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="placeholder"></div>
              </div>
            </div>
          </div>
          <div className="tab_body_container">
            <div className="tab_body card">
              {tab === KBaseTab.FILES && (
                <FilesContentPanel kbase={kbase} workspaceId={workspaceId} />
              )}
              {tab === KBaseTab.WEBPAGES && (
                <WebpagesContentPanel kbase={kbase} workspaceId={workspaceId} />
              )}
              {tab === KBaseTab.TABLES && (
                <TablesContentPanel kbase={kbase} workspaceId={workspaceId} />
              )}
              {tab === KBaseTab.INTEGRATIONS && (
                <IntegrationsContentPanel
                  kbase={kbase}
                  workspaceId={workspaceId}
                />
              )}
            </div>
          </div>
        </>
      )}
    </NewWorkspaceLayout>
  );
};

export default KBaseDetailsPage;
